import replace from 'lodash/replace'
import parseInt from 'lodash/parseInt'
import isNil from 'lodash/isNil'
import isEmpty from 'lodash/isEmpty'
import type { WorkdayConfig } from '@ztechapps/common.contract'
import { DisplayAs } from './DisplayAs'

export * from './DisplayAs'

/**
 * Months conversion factor used by {@link DisplayAs.Months}.
 * A "month" is treated as 4 weeks (in workdays), not a calendar month.
 */
const WEEKS_PER_MONTH = 4

const parseIsoDurationToMinutes = (isoFormat: string): number => {
  const match = isoFormat.match(/^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/)
  if (!match) return 0
  const days = parseInt(match[1] ?? '0', 10)
  const hours = parseInt(match[2] ?? '0', 10)
  const minutes = parseInt(match[3] ?? '0', 10)
  return days * 24 * 60 + hours * 60 + minutes
}

type FormatOptions = {
  displayAs?: DisplayAs
  workdayConfig?: WorkdayConfig
}

const formatAsMinutes = (totalMinutes: number): string => `${totalMinutes}m`

const formatAsHours = (totalMinutes: number): string => {
  if (totalMinutes === 0) return '0h'
  const hour = parseInt(String(totalMinutes / 60))
  const minute = parseInt(String(totalMinutes % 60))
  const parts: string[] = []
  if (hour > 0) parts.push(`${hour}h`)
  if (minute > 0) parts.push(`${minute}m`)
  return parts.join(' ')
}

const formatAsDays = (totalMinutes: number, workdayConfig?: WorkdayConfig): string => {
  if (totalMinutes === 0) return '0h'
  if (!workdayConfig) return formatAsHours(totalMinutes)

  const minutesPerDay = workdayConfig.hoursPerDay * 60
  const day = parseInt(String(totalMinutes / minutesPerDay))
  const dayRemainder = parseInt(String(totalMinutes % minutesPerDay))
  const hour = parseInt(String(dayRemainder / 60))
  const minute = parseInt(String(dayRemainder % 60))

  const parts: string[] = []
  if (day > 0) parts.push(`${day}d`)
  if (hour > 0) parts.push(`${hour}h`)
  if (minute > 0) parts.push(`${minute}m`)
  return !isEmpty(parts) ? parts.join(' ') : '0h'
}

const formatAsWeeks = (totalMinutes: number, workdayConfig?: WorkdayConfig): string => {
  if (totalMinutes === 0) return '0h'
  if (!workdayConfig) return formatAsHours(totalMinutes)

  const { week, day, hour, minute } = Duration.as(totalMinutes, workdayConfig)
  const parts: string[] = []
  if (week > 0) parts.push(`${week}w`)
  if (day > 0) parts.push(`${day}d`)
  if (hour > 0) parts.push(`${hour}h`)
  if (minute > 0) parts.push(`${minute}m`)
  return !isEmpty(parts) ? parts.join(' ') : '0h'
}

const formatAsMonths = (totalMinutes: number, workdayConfig?: WorkdayConfig): string => {
  if (totalMinutes === 0) return '0h'
  if (!workdayConfig) return formatAsHours(totalMinutes)

  const minutesPerDay = workdayConfig.hoursPerDay * 60
  const minutesPerWeek = minutesPerDay * workdayConfig.daysPerWeek
  const minutesPerMonth = minutesPerWeek * WEEKS_PER_MONTH

  const month = parseInt(String(totalMinutes / minutesPerMonth))
  if (month === 0) return formatAsWeeks(totalMinutes, workdayConfig)

  const monthRemainder = parseInt(String(totalMinutes % minutesPerMonth))
  const week = parseInt(String(monthRemainder / minutesPerWeek))
  const weekRemainder = parseInt(String(monthRemainder % minutesPerWeek))
  const day = parseInt(String(weekRemainder / minutesPerDay))
  const dayRemainder = parseInt(String(weekRemainder % minutesPerDay))
  const hour = parseInt(String(dayRemainder / 60))
  const minute = parseInt(String(dayRemainder % 60))

  const parts: string[] = [`${month}mo`]
  if (week > 0) parts.push(`${week}w`)
  if (day > 0) parts.push(`${day}d`)
  if (hour > 0) parts.push(`${hour}h`)
  if (minute > 0) parts.push(`${minute}m`)
  return parts.join(' ')
}

export const Duration = {
  as: (totalMinutes: number, config?: WorkdayConfig) => {
    totalMinutes = Math.max(totalMinutes ?? 0, 0)

    if (config) {
      const minutesPerDay = config.hoursPerDay * 60
      const minutesPerWeek = minutesPerDay * config.daysPerWeek

      const week = parseInt(String(totalMinutes / minutesPerWeek))
      const weekRemainder = parseInt(String(totalMinutes % minutesPerWeek))
      const day = parseInt(String(weekRemainder / minutesPerDay))
      const dayRemainder = parseInt(String(weekRemainder % minutesPerDay))
      const hour = parseInt(String(dayRemainder / 60))
      const minute = parseInt(String(dayRemainder % 60))

      return { week, day, hour, minute }
    }

    const day = parseInt(String(totalMinutes / (60 * 24)))
    const dayRemainder = parseInt(String(totalMinutes % (60 * 24)))
    const hour = parseInt(String(dayRemainder / 60))
    const minute = parseInt(String(dayRemainder % 60))
    return { day, hour, minute }
  },
  /**
   * Formats a duration value according to the selected {@link DisplayAs} mode.
   *
   * Default mode is {@link DisplayAs.Hours}. Edit/parse behavior is not
   * affected by this option — it only changes the display string.
   */
  format: (totalMinutes: number, { displayAs = DisplayAs.Hours, workdayConfig }: FormatOptions = {}): string => {
    const safeMinutes = Math.max(totalMinutes ?? 0, 0)

    switch (displayAs) {
      case DisplayAs.Minutes:
        return formatAsMinutes(safeMinutes)
      case DisplayAs.Hours:
        return formatAsHours(safeMinutes)
      case DisplayAs.Days:
        return formatAsDays(safeMinutes, workdayConfig)
      case DisplayAs.Weeks:
        return formatAsWeeks(safeMinutes, workdayConfig)
      case DisplayAs.Months:
        return formatAsMonths(safeMinutes, workdayConfig)
      default:
        return formatAsHours(safeMinutes)
    }
  },
  isoFormat: (totalMinutes) => {
    const { day, hour, minute } = Duration.as(totalMinutes)
    return `P${day}DT${hour}H${minute}M`
  },
  isoFormat2: (rawString = '') => {
    if (isNil(rawString)) return null
    let changeValue = rawString.replace(/ /g, '')
    changeValue = replace(changeValue, 'days', 'D')
    changeValue = replace(changeValue, 'hr.', 'H')
    changeValue = replace(changeValue, 'min.', 'M')
    changeValue = replace(changeValue, 'd', 'D')
    changeValue = replace(changeValue, 'h', 'H')
    changeValue = replace(changeValue, 'm', 'M')

    //now we are sure that we have proper string here with good delimiter

    const day = parseInt(changeValue.match(/([0-9]+D)/)?.[0] ?? '')
    const hour = parseInt(changeValue.match(/([0-9]+H)/)?.[0] ?? '')
    const min = parseInt(changeValue.match(/([0-9]+M)/)?.[0] ?? '')

    //invalid day so return only starting
    const dayStr = isNaN(day) ? '' : `${day}D`
    const hourStr = isNaN(hour) ? '' : `${hour}H`
    const minStr = isNaN(min) ? '' : `${min}M`

    const datePart = dayStr ? dayStr : ''
    const timePart = hourStr || minStr ? `T${hourStr}${minStr}` : ''

    return datePart || timePart ? `P${datePart}${timePart}` : null
  },

  isValidIsoFormat: (isoDuration) => {
    if (isEmpty(isoDuration)) return false
    const pattern =
      /^(-?)P(?=\d|T\d)(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)([DW]))?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/
    return pattern.test(isoDuration)
  },

  humanize: (totalMinutes: number, config?: WorkdayConfig): string => {
    totalMinutes = Math.max(totalMinutes ?? 0, 0)
    if (totalMinutes === 0) return ''

    const parts: string[] = []

    if (config) {
      const { week, day, hour, minute } = Duration.as(totalMinutes, config)
      if (week > 0) parts.push(`${week} ${week === 1 ? 'week' : 'weeks'}`)
      if (day > 0) parts.push(`${day} ${day === 1 ? 'day' : 'days'}`)
      if (hour > 0) parts.push(`${hour} ${hour === 1 ? 'hour' : 'hours'}`)
      if (minute > 0) parts.push(`${minute} ${minute === 1 ? 'minute' : 'minutes'}`)
    } else {
      const { day, hour, minute } = Duration.as(totalMinutes)
      if (day > 0) parts.push(`${day} ${day === 1 ? 'day' : 'days'}`)
      if (hour > 0) parts.push(`${hour} ${hour === 1 ? 'hour' : 'hours'}`)
      if (minute > 0) parts.push(`${minute} ${minute === 1 ? 'minute' : 'minutes'}`)
    }

    return parts.join(', ')
  },

  parse: (rawString: string, config?: WorkdayConfig): number | null => {
    if (isNil(rawString) || isEmpty(rawString?.trim())) return null

    if (config) {
      // Strict: entire string must only contain valid w/d/h/m tokens with optional whitespace
      const strictPattern = /^\s*(?:(\d+)\s*w)?\s*(?:(\d+)\s*d)?\s*(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?\s*$/i
      const match = rawString.match(strictPattern)
      if (!match || !match.slice(1).some((g) => g !== undefined)) return null

      const minutesPerHour = 60
      const minutesPerDay = config.hoursPerDay * minutesPerHour
      const minutesPerWeek = minutesPerDay * config.daysPerWeek

      const weeks = parseInt(match[1] ?? '0')
      const days = parseInt(match[2] ?? '0')
      const hours = parseInt(match[3] ?? '0')
      const minutes = parseInt(match[4] ?? '0')

      return weeks * minutesPerWeek + days * minutesPerDay + hours * minutesPerHour + minutes
    }

    const isoFormat = Duration.isoFormat2(rawString)
    if (isNil(isoFormat) || !Duration.isValidIsoFormat(isoFormat)) return null

    return parseIsoDurationToMinutes(isoFormat)
  },
}
